<script setup>
import {ref, computed, watch} from 'vue';
import PlayerCard from '~/components/players/PlayerCard.vue';

const props = defineProps({
  label: {
    type: String,
    default: 'Select Users'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  error: {
    type: Boolean,
    default: false
  },
  errorMessages: {
    type: [Array, String],
    default: () => []
  },
  excludeIds: {
    type: Array,
    default: () => []
  },
  hint: {
    type: String,
    default: null
  }
});

const model = defineModel();

// Dialog state
const dialog = ref(false);
const dialogSelection = ref([]);
const searchQuery = ref('');
const debouncedSearchQuery = ref('');
let searchDebounceTimeout = null;

// Fetch selected users for display
const {
  items: selectedUsers,
  getItems: fetchSelectedUsers,
  listPending: loadingUsers
} = useCrudApi('api/v2/users');

// Load selected user details when model changes
const loadUserDetails = async () => {
  if (!model.value || (Array.isArray(model.value) && model.value.length === 0)) {
    selectedUsers.value = [];
    return;
  }

  const ids = Array.isArray(model.value) ? model.value : [model.value];
  await fetchSelectedUsers(1, ids.length, {ids: ids});
};

// Watch model for changes
watch(() => model.value, loadUserDetails, {immediate: true});

// Button text
const buttonText = computed(() => {
  if (!model.value || (Array.isArray(model.value) && model.value.length === 0)) {
    return props.label;
  }

  if (props.multiple) {
    const count = model.value.length;
    return `${count} user${count !== 1 ? 's' : ''} selected`;
  }

  return 'Change user';
});

// Open dialog
const openDialog = () => {
  // Initialize dialog selection with current model value
  if (props.multiple) {
    // Ensure model.value is an array
    if (Array.isArray(model.value)) {
      dialogSelection.value = [...model.value];
    } else if (model.value) {
      // Handle case where model.value is a string (should be array)
      dialogSelection.value = [model.value];
    } else {
      dialogSelection.value = [];
    }
  } else {
    dialogSelection.value = model.value ? [model.value] : [];
  }

  searchQuery.value = '';
  debouncedSearchQuery.value = '';
  dialog.value = true;
};

// Confirm selection
const confirmSelection = () => {
  if (props.multiple) {
    model.value = [...dialogSelection.value];
  } else {
    model.value = dialogSelection.value.length > 0 ? dialogSelection.value[0] : null;
  }

  dialog.value = false;
};

// Debounce search query
watch(searchQuery, (newValue) => {
  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
  }

  searchDebounceTimeout = setTimeout(() => {
    debouncedSearchQuery.value = newValue;
  }, 300);
});

// Filters for user search - reactive computed
const customFilters = computed(() => {
  const filters = {
    fromWebSearch: true
  };

  if (debouncedSearchQuery.value && debouncedSearchQuery.value.trim()) {
    filters.keywords = debouncedSearchQuery.value.trim();
  }

  if (props.excludeIds && props.excludeIds.length > 0) {
    filters.excludeIds = props.excludeIds;
  }

  return filters;
});

// Check if user is selected
const isUserSelected = (userId) => {
  return dialogSelection.value.includes(userId);
};

// Toggle user selection in dialog
const toggleUser = (userId) => {
  const index = dialogSelection.value.indexOf(userId);

  if (props.multiple) {
    if (index > -1) {
      dialogSelection.value.splice(index, 1);
    } else {
      dialogSelection.value.push(userId);
    }
  } else {
    // Single selection - replace
    dialogSelection.value = index > -1 ? [] : [userId];
  }
};

// Dynamic button text for dialog
const confirmButtonText = computed(() => {
  const count = dialogSelection.value.length;
  if (count === 0) {
    return 'Confirm Selection';
  }
  return `Confirm ${count} user${count !== 1 ? 's' : ''}`;
});

// Dynamic dialog title
const dialogTitle = computed(() => {
  return props.multiple ? 'Select Users' : 'Select User';
});
</script>

<template>
  <div class="user-select-field">
    <!-- Label -->
    <label v-if="label" class="tl-label mb-1 d-block">{{ label }}</label>

    <!-- Display area and button row -->
    <div class="d-flex gap-2 align-start">
      <!-- Display area -->
      <div
          class="user-select-display pa-2 flex-grow-1 d-flex align-center"
          :class="{ 'border-error': error, 'border-hairline': !error }"
      >
        <div class="flex-fill">
          <!-- Loading state -->
          <div v-if="loadingUsers" class="text-center pa-2">
            <v-progress-circular indeterminate size="20" color="primary"/>
          </div>

          <!-- Selected users -->
          <div v-else-if="selectedUsers.length > 0" class="d-flex flex-column gap-1">
            <player-card
                v-for="user in selectedUsers"
                :key="user.id"
                :player="user"
                size="small"
                :expandable="false"
            />
          </div>

          <!-- Empty state -->
          <div v-else class="text-center pa-2 d-flex flex-row gap-2">
            <v-icon size="24" color="textSubtle">mdi-account-off</v-icon>
            <div class="text-caption text-medium-emphasis">No users selected</div>
          </div>

        </div>
        <!-- Select button -->
        <v-btn
            size="small"
            variant="outlined"
            color="primary"
            @click="openDialog"
        >
          {{ buttonText }}
        </v-btn>
      </div>
    </div>

    <!-- Hint text -->
    <div v-if="hint && !error" class="text-caption text-medium-emphasis mt-1">
      {{ hint }}
    </div>

    <!-- Error messages -->
    <div v-if="error" class="text-caption text-error mt-1">
      <div v-if="Array.isArray(errorMessages)">
        <div v-for="(msg, idx) in errorMessages" :key="idx">{{ msg }}</div>
      </div>
      <div v-else>{{ errorMessages }}</div>
    </div>

    <!-- Selection Dialog -->
    <crud-form-dialog
        :full-screen="true"
        :dialog="dialog"
        @update:dialog="dialog = $event"
        v-model="dialogSelection"
        :save-action="confirmSelection"
        action="create"
        :create-title="dialogTitle"
        :btn-text="confirmButtonText"
        btn-icon="mdi-check"
    >
      <template #default>
        <div class="user-select-dialog-content">
          <!-- Search input - Fixed at top -->
          <div class="search-section border-b">
            <v-text-field
                v-model="searchQuery"
                label="Search users"
                placeholder="Search by name or email..."
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                density="comfortable"
                clearable
                hide-details
            />
          </div>

          <!-- User list - Scrollable -->
          <div class="user-list-section">
            <crud-list-loader
                :per-page="20"
                :custom-filters="customFilters"
                path="api/v2/users"
            >
              <template #default="{ items, listPending }">
                <!-- Loading -->
                <div v-if="listPending" class="text-center pa-8">
                  <v-progress-circular indeterminate color="primary"/>
                </div>

                <!-- No results -->
                <div v-else-if="items.length === 0" class="text-center pa-8">
                  <v-icon size="48" color="textSubtle">mdi-account-search</v-icon>
                  <div class="text-body-1 mt-2">No users found</div>
                  <div class="text-caption text-medium-emphasis">Try a different search term</div>
                </div>

                <!-- Results list -->
                <v-list v-else class="pa-0">
                  <v-list-item
                      v-for="user in items"
                      :key="user.id"
                      @click="toggleUser(user.id)"
                      class="px-0"
                  >
                    <template #prepend>
                      <v-checkbox-btn
                          :model-value="isUserSelected(user.id)"
                          :hide-details="true"
                          @click.stop="toggleUser(user.id)"
                      />
                    </template>

                    <player-card
                        :player="user"
                        size="small"
                        :expandable="false"
                    />
                  </v-list-item>
                </v-list>
              </template>
            </crud-list-loader>
          </div>
        </div>
      </template>
    </crud-form-dialog>
  </div>
</template>

<style scoped>
.user-select-display {
  background-color: rgb(var(--v-theme-surface));
  min-height: 48px;
  border-radius: 14px;
}

.border-hairline {
  border: 1px solid rgb(var(--v-theme-borderLight));
}

.border-error {
  border: 1px solid rgb(var(--v-theme-error));
}

.gap-1 > * + * {
  margin-top: 4px;
}

.gap-2 {
  gap: 8px;
}

/* Dialog content layout */
.user-select-dialog-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0; /* Important for flex children to allow shrinking */
}

.search-section {
  flex-shrink: 0; /* Fixed at top */
  padding-bottom: 16px;
}

.user-list-section {
  flex: 1 1 0; /* Take remaining space */
  min-height: 0; /* Allow shrinking */
  overflow-y: auto; /* Scroll when content overflows */
}
</style>
